import { useRef, useEffect, useState, useMemo } from 'react';
import type * as React from 'react';
import type { Seedling } from '../lib/types';

// Weird, beautiful, non-cliché granular emotions — systems + transition residue, not wellness.
export const GRANULAR_EMOTIONS: string[] = [
  'Recursive ache',
  'Fierce clarity',
  'Muffled rupture',
  'Liminal debt',
  'Startled recognition',
  'Hollow competence',
  'Tangled belonging',
  'Slow unspooling',
  'Embodied refusal',
  'Ambiguous relief',
  'Frayed loyalty',
  'Quiet severance',
  'Echoing absence',
  'Fractured competence',
  'Sharp mercy',
  'Dull astonishment',
  'Tender severance',
  'Resonant shame',
  'Inverted gratitude',
  'Waiting that names itself',
  'Loop made visible',
  'Leverage misread',
  'Boundary that dissolved',
  'Cost that surfaced late',
];

interface Props {
  seedlings: Seedling[];
  onSegmentClick?: (emotion: string | null) => void;
  activeFilter?: string | null;
  className?: string;
}

export function EmotionWheel({ seedlings, onSegmentClick, activeFilter, className = '' }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; count: number; avgRepair: number } | null>(null);

  // Build counts + weighted score (count * repair factor)
  const emotionData = useMemo(() => {
    const map = new Map<string, { count: number; totalRepair: number; seedlings: Seedling[] }>();
    GRANULAR_EMOTIONS.forEach(e => map.set(e, { count: 0, totalRepair: 0, seedlings: [] }));

    seedlings.forEach(s => {
      const tags = s.emotion_tags && s.emotion_tags.length ? s.emotion_tags : deriveEmotionTags(s);
      const repair = s.repair_readiness ?? 45; // neutral default so empty trays still show texture
      tags.forEach(tag => {
        if (!map.has(tag)) return;
        const entry = map.get(tag)!;
        entry.count += 1;
        entry.totalRepair += repair;
        entry.seedlings.push(s);
      });
    });

    return GRANULAR_EMOTIONS.map(emotion => {
      const d = map.get(emotion)!;
      const avgRepair = d.count > 0 ? Math.round(d.totalRepair / d.count) : 0;
      return { emotion, count: d.count, avgRepair, seedlings: d.seedlings };
    });
  }, [seedlings]);

  const maxCount = Math.max(1, ...emotionData.map(d => d.count));

  function deriveEmotionTags(s: Seedling): string[] {
    // Very small, visible, local heuristic only — never presented as smart.
    const text = (s.content + ' ' + (s.impact || '') + ' ' + (s.accountability || '')).toLowerCase();
    const tags: string[] = [];
    if (/(loop|cycle|repeat|again)/.test(text)) tags.push('Loop made visible');
    if (/(miss|late|never|excluded|absent)/.test(text)) tags.push('Leverage misread', 'Echoing absence');
    if (/(cost|bill|paid|carried|unseen)/.test(text)) tags.push('Cost that surfaced late', 'Tangled belonging');
    if (/(boundary|time zone|timezone|who decides)/.test(text)) tags.push('Boundary that dissolved');
    if (/(shame|quiet|never said|out loud)/.test(text)) tags.push('Resonant shame');
    if (tags.length === 0) tags.push(s.repair_readiness && s.repair_readiness > 60 ? 'Fierce clarity' : 'Slow unspooling');
    return tags.slice(0, 2);
  }

  // Draw the wheel
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const size = 520;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;
    const outerR = 232;
    const innerR = 78;

    ctx.clearRect(0, 0, size, size);

    const n = GRANULAR_EMOTIONS.length;
    const angleStep = (Math.PI * 2) / n;

    // Background mycelial soil ring
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, outerR + 18, 0, Math.PI * 2);
    ctx.fillStyle = '#EDE6D9';
    ctx.fill();

    // Subtle mycelium threads
    ctx.strokeStyle = 'rgba(139,94,60,0.12)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 7; i++) {
      ctx.beginPath();
      ctx.arc(cx + (i - 3) * 3.5, cy + Math.sin(i) * 4, outerR + 9 + (i % 3), 0.2, Math.PI * 1.8);
      ctx.stroke();
    }
    ctx.restore();

    emotionData.forEach((d, i) => {
      const start = -Math.PI / 2 + i * angleStep;
      const end = start + angleStep - 0.012; // tiny gap

      const weight = d.count / maxCount;
      const r = innerR + (outerR - innerR) * (0.35 + weight * 0.65);
      const isActive = activeFilter === d.emotion;
      const isHover = hovered === d.emotion;

      // Wedge
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(start) * innerR, cy + Math.sin(start) * innerR);
      ctx.arc(cx, cy, r, start, end);
      ctx.lineTo(cx + Math.cos(end) * innerR, cy + Math.sin(end) * innerR);
      ctx.arc(cx, cy, innerR, end, start, true);
      ctx.closePath();

      // Color: earthy with lavender/rust for high repair or active
      let hue = 32 + (i % 7) * 3; // rust-soil range
      let sat = 28 + Math.min(32, d.avgRepair * 0.18);
      let light = 46 + (d.count > 0 ? 6 : -4);

      if (isActive) { sat = 62; light = 38; }
      if (isHover) light = Math.min(62, light + 9);

      ctx.fillStyle = `hsl(${hue}, ${sat}%, ${light}%)`;
      ctx.fill();

      // Edge
      ctx.strokeStyle = isActive ? '#6B5B8C' : 'rgba(44,37,34,0.35)';
      ctx.lineWidth = isActive ? 2.5 : 1;
      ctx.stroke();
      ctx.restore();

      // Small radial label tick + abbreviated name when significant
      const mid = start + (end - start) / 2;
      const labelR = innerR + (r - innerR) * 0.72;
      const lx = cx + Math.cos(mid) * labelR;
      const ly = cy + Math.sin(mid) * labelR;

      if (d.count > 0 || isActive) {
        ctx.save();
        ctx.fillStyle = isActive ? '#2C2522' : '#5C5248';
        ctx.font = isActive ? '600 10px ui-serif, Georgia, serif' : '500 9px ui-serif, Georgia, serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const short = d.emotion.length > 18 ? d.emotion.slice(0, 15) + '…' : d.emotion;
        // rotate text to follow arc roughly
        ctx.translate(lx, ly);
        ctx.rotate(mid + (mid > 0 ? 0.5 : -0.5));
        ctx.fillText(short, 0, 0);
        ctx.restore();
      }

      // Repair indicator dot on rim for high readiness
      if (d.avgRepair > 55) {
        const dotR = innerR + (r - innerR) * 0.96;
        const dx = cx + Math.cos(mid) * dotR;
        const dy = cy + Math.sin(mid) * dotR;
        ctx.beginPath();
        ctx.arc(dx, dy, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = '#C5A46E';
        ctx.fill();
      }
    });

    // Center label — the asset
    ctx.save();
    ctx.fillStyle = '#2C2522';
    ctx.font = '600 13px ui-serif, Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText('EMOTION', cx, cy - 7);
    ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    ctx.fillStyle = '#8A7F72';
    ctx.fillText('GRANULARITY', cx, cy + 8);
    ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    ctx.fillText(`${seedlings.length} seedlings`, cx, cy + 26);
    ctx.restore();

    // Subtle outer ring
    ctx.beginPath();
    ctx.arc(cx, cy, outerR + 2, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(139,94,60,0.25)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }, [emotionData, maxCount, activeFilter, hovered]);

  // Hit testing + interaction
  function handleCanvasEvent(e: React.MouseEvent | React.TouchEvent) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? (e as any).touches[0].clientX : (e as any).clientX;
    const clientY = 'touches' in e ? (e as any).touches[0].clientY : (e as any).clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    if (dist < 38 || dist > 248) {
      setHovered(null);
      setTooltip(null);
      return;
    }

    const n = GRANULAR_EMOTIONS.length;
    let idx = Math.floor(((angle + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2)) / (Math.PI * 2 / n));
    idx = (idx + n) % n;

    const d = emotionData[idx];
    setHovered(d.emotion);

    if ('clientX' in e) {
      setTooltip({
        x: (e as React.MouseEvent).clientX,
        y: (e as React.MouseEvent).clientY,
        label: d.emotion,
        count: d.count,
        avgRepair: d.avgRepair,
      });
    }
  }

  function handleClick() {
    if (!hovered || !onSegmentClick) return;
    const next = activeFilter === hovered ? null : hovered;
    onSegmentClick(next);
  }

  function clearFilter() {
    onSegmentClick && onSegmentClick(null);
    setHovered(null);
    setTooltip(null);
  }

  return (
    <div className={`relative select-none ${className}`}>
      <div className="flex items-center justify-between mb-2 px-1">
        <div>
          <div className="text-[10px] uppercase tracking-[2px] text-[var(--text-subtle)]">emotion granularity wheel</div>
          <div className="text-xs text-[var(--text-muted)] -mt-0.5">click a petal to filter the tray • sized by presence + repair readiness</div>
        </div>
        {activeFilter && (
          <button onClick={clearFilter} className="text-xs px-3 py-1 rounded-xl border border-[var(--border)] hover:bg-[var(--bg-elev)]">
            clear filter — showing all
          </button>
        )}
      </div>

      <div className="relative inline-block" style={{ filter: 'contrast(0.98)' }}>
        <canvas
          ref={canvasRef}
          onMouseMove={handleCanvasEvent}
          onMouseLeave={() => { setHovered(null); setTooltip(null); }}
          onClick={handleClick}
          onTouchStart={handleCanvasEvent}
          onTouchEnd={handleClick}
          className="cursor-pointer touch-none rounded-full"
          style={{ background: 'transparent' }}
        />
        {/* center soil depression visual */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92px] h-[92px] rounded-full border border-[#C9BBA8] pointer-events-none" style={{ boxShadow: 'inset 0 2px 8px rgba(44,37,34,0.15)' }} />
      </div>

      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none rounded-xl border bg-[var(--bg-elev)] px-3 py-1.5 text-xs shadow"
          style={{ left: tooltip.x + 14, top: tooltip.y + 14 }}
        >
          <div className="font-medium">{tooltip.label}</div>
          <div className="text-[var(--text-subtle)] tabular-nums">
            {tooltip.count} seedling{tooltip.count === 1 ? '' : 's'} • avg repair readiness {tooltip.avgRepair}
          </div>
        </div>
      )}

      <div className="mt-2 text-[10px] text-[var(--text-subtle)] max-w-[46ch] leading-snug">
        These are not basic emotion words. They are the textures that actually survived the transition.
        Sizing and dots reflect how many seedlings carry the texture + how ready the group felt to act on them.
      </div>
    </div>
  );
}
